import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Form, Card, Accordion, ProgressBar } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import Loading from "../../../../../components/general/Loading";
import {
  handleError,
  isNotBusinessError,
} from "../../../../../utils/handleError";
import {
  getDistricts,
  getCountiesByDistrictCode,
} from "../../../../../rest/administrativeDivision";
import {
  getIndicatorCandidaturesAdCounter,
  getIndicatorCandidaturesStatesAdCounter,
  getIndicatorCandidaturesGenderAdCounter,
  getIndicatorCandidaturesAgesAdCounter,
  getIndicatorCandidaturesSchoolQualificationAdCounter,
  getIndicatorCandidaturesQualificationLevelsAdCounter,
} from "../../../../../rest/indicator";
import ExportDialog from "../utils/ExportDialog";
import LastUpdate from "../utils/LastUpdate";

import * as XLSX from "xlsx";
import { buildWorkbookAdministrativeDivision } from "../sectionsIndicatorsCandidatures/exporters/exporterAdministrativeDivision";

import { useClientPdf } from "../utils/useClientPdf";

export default function IndicatorAdministrativeDivision() {
  const intl = useIntl();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [districts, setDistricts] = useState([]);
  const [counties, setCounties] = useState([]);

  const [districtCode, setDistrictCode] = useState(null);
  const [countyCode, setCountyCode] = useState(null);

  const [dataLocal, setDataLocal] = useState([]);
  const [dataStates, setDataStates] = useState([]);
  const [dataGender, setDataGender] = useState([]);
  const [dataAge, setDataAge] = useState([]);
  const [dataSchool, setDataSchool] = useState([]);
  const [dataQualification, setDataQualification] = useState([]);

  const [showExport, setShowExport] = useState(false);

  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(null);

  const { pdfRef, exportPdf } = useClientPdf({
    marginMm: 10,
    orientation: "portrait",
    hideSelectors: ['[data-hide-on-export="true"]', ".modal", ".toast"],
  });

  const sumBy = (arr, key = "counter") =>
    arr?.reduce((s, i) => s + (i?.[key] || 0), 0) || 0;
  const toPct = (v, t) => (t > 0 ? Math.round((v / t) * 100) : 0);

  const pickLatestMeta = (list) => {
    const xs = list
      .map(({ ts, ver }) => ({
        ts: ts || null,
        ver: ver || null,
        epoch: ts ? Date.parse(ts) : NaN,
      }))
      .filter((x) => !Number.isNaN(x.epoch));
    if (xs.length === 0) return { ts: null, ver: null };
    xs.sort((a, b) => b.epoch - a.epoch);
    return { ts: xs[0].ts, ver: xs[0].ver };
  };

  const aggregateBy = (items, keyFn, labelFn) => {
    const map = new Map();
    for (const it of items || []) {
      const k = keyFn(it);
      const prev = map.get(k);
      const counter = (it?.counter || 0) + (prev?.counter || 0);
      const label = prev?.label ?? labelFn(it);
      map.set(k, { key: k, label, counter });
    }
    return Array.from(map.values());
  };

  const genderLabelNode = (g) => {
    const v = String(g ?? "").trim();
    if (v === "0")
      return (
        <FormattedMessage
          id="indicator.genderForm.M"
          defaultMessage="Masculino"
        />
      );
    if (v === "1")
      return (
        <FormattedMessage
          id="indicator.genderForm.F"
          defaultMessage="Feminino"
        />
      );
    return (
      <FormattedMessage
        id={`promoterForm.gender.${v}`}
        defaultMessage={v || "-"}
      />
    );
  };

  const ageLabelFromToken = (token) => {
    const v = String(token ?? "")
      .trim()
      .toUpperCase();
    if (v === "65_PLUS") return "> 65";
    if (v.includes("_")) return v.replace("_", "-");
    return v || "-";
  };
  const ageLabelNode = (token) => <span>{ageLabelFromToken(token)}</span>;

  const localItems = useMemo(() => {
    const items = buildLocalItems();
    return items.sort((a, b) => b.counter - a.counter);
  }, [dataLocal, districtCode, countyCode]);

  const totalLocal = useMemo(() => sumBy(localItems), [localItems]);

  function buildLocalItems() {
    if (Array.isArray(dataLocal) && dataLocal.length > 0) {
      return aggregateBy(
        dataLocal,
        (it) => String(it.description ?? it.code ?? "-"),
        (it) => it.description ?? it.code ?? "-"
      );
    }
    return [];
  }

  const getFilterCode = () => countyCode || districtCode || null;
  const getLevel = () => (countyCode ? "COUNTY" : "DISTRICT");

  const resetAllData = () => {
    setDataLocal([]);
    setDataStates([]);
    setDataGender([]);
    setDataAge([]);
    setDataSchool([]);
    setDataQualification([]);
    setLastUpdate(null);
    setRefreshVersion(null);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const { data: dist } = await getDistricts();
        const ordered = (dist || []).slice().sort((a, b) => {
          const la = (a?.description || "").localeCompare?.(
            b?.description || "",
            "pt-PT"
          );
          return la === 0 ? String(a?.code).localeCompare(String(b?.code)) : la;
        });
        setDistricts(ordered);
        resetAllData();
      } catch (err) {
        if (isNotBusinessError(err)) setError(err);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    const code = getFilterCode();
    if (!code) {
      resetAllData();
      return;
    }
    fetchAllData(code, getLevel());
  }, [districtCode, countyCode]);

  const fetchAllData = async (code, level) => {
    setLoading(true);
    resetAllData();
    try {
      const [pLocal, pStates, pGender, pAge, pSchool, pQual] =
        await Promise.all([
          getIndicatorCandidaturesAdCounter(code, level).then(
            (r) => r?.data || {}
          ),
          getIndicatorCandidaturesStatesAdCounter(code, level).then(
            (r) => r?.data || {}
          ),
          getIndicatorCandidaturesGenderAdCounter(code, level).then(
            (r) => r?.data || {}
          ),
          getIndicatorCandidaturesAgesAdCounter(code, level).then(
            (r) => r?.data || {}
          ),
          getIndicatorCandidaturesSchoolQualificationAdCounter(
            code,
            level
          ).then((r) => r?.data || {}),
          getIndicatorCandidaturesQualificationLevelsAdCounter(
            code,
            level
          ).then((r) => r?.data || {}),
        ]);

      setDataLocal(pLocal.data || []);
      setDataStates(pStates.data || []);
      setDataGender(pGender.data || []);
      setDataAge(pAge.data || []);
      setDataSchool(pSchool.data || []);
      setDataQualification(pQual.data || []);

      const { ts, ver } = pickLatestMeta([
        { ts: pLocal.lastRefreshedAt, ver: pLocal.refreshVersion },
        { ts: pStates.lastRefreshedAt, ver: pStates.refreshVersion },
        { ts: pGender.lastRefreshedAt, ver: pGender.refreshVersion },
        { ts: pAge.lastRefreshedAt, ver: pAge.refreshVersion },
        { ts: pSchool.lastRefreshedAt, ver: pSchool.refreshVersion },
        { ts: pQual.lastRefreshedAt, ver: pQual.refreshVersion },
      ]);
      setLastUpdate(ts);
      setRefreshVersion(ver);
    } catch (err) {
      if (isNotBusinessError(err)) setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDistrictChange = async (e) => {
    const code = e.target.value === "-1" ? null : e.target.value;
    setDistrictCode(code);
    setCountyCode(null);

    if (!code) {
      setCounties([]);
      resetAllData();
      return;
    }
    try {
      const { data } = await getCountiesByDistrictCode(code);
      const ordered = (data || []).slice().sort((a, b) => {
        const la = (a?.description || "").localeCompare?.(
          b?.description || "",
          "pt-PT"
        );
        return la === 0 ? String(a?.code).localeCompare(String(b?.code)) : la;
      });
      setCounties(ordered);
    } catch (err) {
      if (isNotBusinessError(err)) setError(err);
    }
  };

  const handleCountyChange = (e) => {
    const code = e.target.value === "-1" ? null : e.target.value;
    setCountyCode(code);
  };

  if (loading) return <Loading />;
  if (error) return handleError(error);

  const canShow = Boolean(districtCode);
  const shouldAggregate = canShow && !countyCode;

  const statesView = (
    shouldAggregate
      ? aggregateBy(
          dataStates,
          (it) => it.state,
          (it) => (
            <FormattedMessage
              id={`candidatureState.${it.state}`}
              defaultMessage={it.state}
            />
          )
        )
      : (dataStates || []).map((it) => ({
          key: it.state,
          label: (
            <FormattedMessage
              id={`candidatureState.${it.state}`}
              defaultMessage={it.state}
            />
          ),
          counter: it.counter,
        }))
  ).sort((a, b) => b.counter - a.counter);

  const genderView = (
    shouldAggregate
      ? aggregateBy(
          dataGender,
          (it) => String(it.gender),
          (it) => genderLabelNode(it.gender)
        )
      : (dataGender || []).map((it) => ({
          key: String(it.gender),
          label: genderLabelNode(it.gender),
          counter: it.counter,
        }))
  ).sort((a, b) => b.counter - a.counter);

  const ageView = (
    shouldAggregate
      ? aggregateBy(
          dataAge,
          (it) => String(it.secondGroupFilter ?? ""),
          (it) => ageLabelNode(it.secondGroupFilter)
        )
      : (dataAge || []).map((it) => ({
          key: String(it.secondGroupFilter ?? ""),
          label: ageLabelNode(it.secondGroupFilter),
          counter: it.counter,
        }))
  ).sort((a, b) => b.counter - a.counter);

  const schoolView = (
    shouldAggregate
      ? aggregateBy(
          dataSchool,
          (it) => String(it.secondGroupFilter ?? ""),
          (it) => (
            <FormattedMessage
              id={`schoolQualification.${it.secondGroupFilter}`}
              defaultMessage={String(it.secondGroupFilter ?? "")}
            />
          )
        )
      : (dataSchool || []).map((it) => ({
          key: String(it.secondGroupFilter ?? ""),
          label: (
            <FormattedMessage
              id={`schoolQualification.${it.secondGroupFilter}`}
              defaultMessage={String(it.secondGroupFilter ?? "")}
            />
          ),
          counter: it.counter,
        }))
  ).sort((a, b) => b.counter - a.counter);

  const qualView = (
    shouldAggregate
      ? aggregateBy(
          dataQualification,
          (it) => String(it.code ?? "-"),
          (it) => it.code ?? "-"
        )
      : (dataQualification || []).map((it) => ({
          key: String(it.code ?? "-"),
          label: it.code ?? "-",
          counter: it.counter,
        }))
  ).sort((a, b) => b.counter - a.counter);

  const handleExportExcelFinal = async () => {
    try {
      if (!canShow) return;
      const wb = await buildWorkbookAdministrativeDivision({
        code: getFilterCode(),
        level: getLevel(),
        intl,
      });
      const nomeArquivo = countyCode
        ? "indicadores_divisao_administrativa_freguesias.xlsx"
        : "indicadores_divisao_administrativa_concelhos.xlsx";
      XLSX.writeFile(wb, nomeArquivo);
      setShowExport(false);
    } catch (e) {
      console.error("[Excel AD] falhou:", e);
      alert("Falha ao gerar Excel: " + (e?.message || e));
    }
  };

  const handleExportPdfClient = async () => {
    try {
      if (!canShow) return;
      const nomeArquivo = countyCode
        ? "indicadores_divisao_administrativa_freguesias.pdf"
        : "indicadores_divisao_administrativa_concelhos.pdf";
      await exportPdf(nomeArquivo);
      setShowExport(false);
    } catch (e) {
      console.error("[PDF AD] falhou:", e);
      alert("Falha ao gerar PDF: " + (e?.message || e));
    }
  };

  const SectionHeader = ({ title, right }) => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <h6 className="text-secondary mb-0">{title}</h6>
      {right && <div className="text-muted">{right}</div>}
    </div>
  );

  const BarRow = ({ labelLeft, value, total }) => {
    const pct = toPct(value, total);
    return (
      <div className="mb-2">
        <div className="d-flex justify-content-between">
          <div
            className="text-truncate"
            title={typeof labelLeft === "string" ? labelLeft : undefined}
          >
            {labelLeft}
          </div>
          <div className="ms-2">
            <strong>{value}</strong>{" "}
            <span className="text-muted">({pct}%)</span>
          </div>
        </div>
        <ProgressBar now={pct} />
      </div>
    );
  };

  const DistCard = ({ title, items, totalSection }) => (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <SectionHeader
          title={title}
          right={
            <>
              <span className="me-1">Total:</span>
              <strong>{totalSection}</strong>
            </>
          }
        />
        {items.length === 0 && (
          <div className="text-muted">Sem dados</div>
        )}
        {items.map((it) => (
          <BarRow
            key={it.key}
            labelLeft={it.label}
            value={it.counter}
            total={totalSection}
          />
        ))}
      </Card.Body>
    </Card>
  );

  return (
    <div className="container-fluid" ref={pdfRef}>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="text-secondary mb-0">
            <FormattedMessage
              id="indicator.candidatures.byAdministrativeDivision"
              defaultMessage="Candidaturas por Divisão Administrativa"
            />
          </h4>
          <small className="text-muted d-block">
            Este indicador contabiliza promotores activos (uma candidatura pode ter mais do que um promotor).
          </small>
          <small className="text-muted">
            O total pode ser inferior ao total global da Medida quando existem candidaturas sem localização preenchida.
          </small>
        </Col>

        <Col className="text-end">
          {canShow ? (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowExport(true);
              }}
              style={{ color: "#e5533d", textDecoration: "underline" }}
              data-hide-on-export="true"
            >
              <FormattedMessage
                id="indicator.exportar"
                defaultMessage="Exportar"
              />
            </a>
          ) : (
            <>
              <span
                aria-disabled="true"
                style={{
                  color: "#adb5bd",
                  textDecoration: "underline",
                  cursor: "not-allowed",
                }}
                title="Selecione um distrito para exportar"
                data-hide-on-export="true"
              >
                <FormattedMessage
                  id="indicator.exportar"
                  defaultMessage="Exportar"
                />
              </span>
              <div className="text-muted mt-1" data-hide-on-export="true">
                <FormattedMessage
                  id="indicator.exportar.info.selecioneDistrito"
                  defaultMessage="Selecione um distrito para ativar a exportação."
                />
              </div>
            </>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id="indicator.promoterForm.district"
                defaultMessage="Distrito"
              />
            </Form.Label>
            <Form.Select
              value={districtCode || "-1"}
              onChange={handleDistrictChange}
            >
              <option value="-1">
                <FormattedMessage
                  id="all.chooseOne"
                  defaultMessage="Escolha uma opção"
                />
              </option>
              {districts.map((d) => (
                <option key={d.code} value={d.code}>
                  {d.description}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id="indicator.promoterForm.county"
                defaultMessage="Concelho"
              />
            </Form.Label>
            <Form.Select
              value={countyCode || "-1"}
              onChange={handleCountyChange}
              disabled={!districtCode}
            >
              <option value="-1">
                <FormattedMessage
                  id="all.chooseOne"
                  defaultMessage="Escolha uma opção"
                />
              </option>
              {counties.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.description}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {!canShow && (
        <div className="text-muted">
          <FormattedMessage
            id="indicator.ad.selecioneDistrito"
            defaultMessage="Selecione um distrito para visualizar os indicadores."
          />
        </div>
      )}

      {canShow && (
        <Accordion defaultActiveKey="0" alwaysOpen>
          <Accordion.Item eventKey="0">
            <Accordion.Header>Estado & Género</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col lg={6}>
                  <DistCard
                    title={intl.formatMessage({
                      id: "indicator.state.column",
                      defaultMessage: "Por Estado",
                    })}
                    items={statesView}
                    totalSection={sumBy(statesView)}
                  />
                </Col>
                <Col lg={6}>
                  <DistCard
                    title={intl.formatMessage({
                      id: "indicator.gender.column",
                      defaultMessage: "Género",
                    })}
                    items={genderView}
                    totalSection={sumBy(genderView)}
                  />
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="1">
            <Accordion.Header>Grupo Etário & Habilitação</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col lg={6}>
                  <DistCard
                    title={intl.formatMessage({
                      id: "indicator.ageGroup.column",
                      defaultMessage: "Grupo Etário",
                    })}
                    items={ageView}
                    totalSection={sumBy(ageView)}
                  />
                </Col>
                <Col lg={6}>
                  <DistCard
                    title={intl.formatMessage({
                      id: "indicator.schoolQualification.column",
                      defaultMessage: "Habilitação Escolar",
                    })}
                    items={schoolView}
                    totalSection={sumBy(schoolView)}
                  />
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="2">
            <Accordion.Header>Nível de Qualificação & Local</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col lg={6}>
                  <DistCard
                    title={intl.formatMessage({
                      id: "indicator.qualificationLevel.column",
                      defaultMessage: "Nível de Qualificação",
                    })}
                    items={qualView}
                    totalSection={sumBy(qualView)}
                  />
                </Col>
                <Col lg={6}>
                  <Card className="mb-3 shadow-sm">
                    <Card.Body>
                      <SectionHeader
                        title={
                          countyCode
                            ? intl.formatMessage({
                                id: "indicator.local.parish.top",
                                defaultMessage:
                                  "Candidaturas por Freguesia (Top 10)",
                              })
                            : intl.formatMessage({
                                id: "indicator.local.county.top",
                                defaultMessage:
                                  "Candidaturas por Concelho (Top 10)",
                              })
                        }
                        right={
                          <>
                            <span className="me-1">Total:</span>
                            <strong>{totalLocal}</strong>
                          </>
                        }
                      />
                      {localItems.length === 0 && (
                        <div className="text-muted">Sem dados</div>
                      )}
                      {localItems.slice(0, 10).map((it) => (
                        <BarRow
                          key={it.key}
                          labelLeft={it.label}
                          value={it.counter}
                          total={totalLocal}
                        />
                      ))}
                      <div className="d-flex justify-content-end mt-2">
                        <span className="text-muted">Top 10</span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}

      {canShow && lastUpdate && (
        <LastUpdate
          iso={lastUpdate}
          version={refreshVersion}
          className="mt-3"
        />
      )}

      <ExportDialog
        show={showExport}
        onHide={() => setShowExport(false)}
        options={[
          {
            onClick: handleExportExcelFinal,
            filename: countyCode
              ? "indicadores_divisao_administrativa_freguesias.xlsx"
              : "indicadores_divisao_administrativa_concelhos.xlsx",
          },
          {
            onClick: handleExportPdfClient,
            filename: countyCode
              ? "indicadores_divisao_administrativa_freguesias.pdf"
              : "indicadores_divisao_administrativa_concelhos.pdf",
          },
        ]}
      />
    </div>
  );
}
